import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/data/videos";
import { formatPlayCount, getCategoryName } from "@/data/videos";

interface HeroCarouselProps {
  videos: Video[];
}

const heroCovers: Record<string, string> = {
  t1: "https://readdy.ai/api/search-image?query=Ancient%20Chinese%20mountain%20sect%20with%20floating%20mist%20and%20a%20swordsman%20in%20flowing%20robes%20gazing%20at%20a%20golden%20sunrise%2C%20cinematic%20color%20grading%2C%20dramatic%20lighting%2C%20atmospheric%20mood%2C%20dark%20textured%20backdrop%20with%20soft%20glow%2C%20ultra%20detailed%2C%20professional%20film%20still%2C%2016:9%20widescreen%20composition&width=1600&height=900&seq=hero-t1&orientation=landscape",
  v1: "https://readdy.ai/api/search-image?query=Colorful%20bright%20comedy%20stage%20with%20confetti%20and%20a%20jubilant%20host%20silhouette%20under%20warm%20spotlights%2C%20cinematic%20color%20grading%2C%20dramatic%20lighting%2C%20atmospheric%20mood%2C%20dark%20textured%20backdrop%20with%20soft%20glow%2C%20ultra%20detailed%2C%20professional%20film%20still%2C%2016:9%20widescreen%20composition&width=1600&height=900&seq=hero-v1&orientation=landscape",
  d3: "https://readdy.ai/api/search-image?query=Rustic%20Chinese%20village%20kitchen%20with%20steaming%20street%20food%20and%20warm%20morning%20light%20streaming%20in%2C%20cinematic%20color%20grading%2C%20dramatic%20lighting%2C%20atmospheric%20mood%2C%20dark%20textured%20backdrop%20with%20soft%20glow%2C%20ultra%20detailed%2C%20professional%20film%20still%2C%2016:9%20widescreen%20composition&width=1600&height=900&seq=hero-d3&orientation=landscape",
  v5: "https://readdy.ai/api/search-image?query=Moody%20comedy%20club%20with%20a%20single%20spotlight%20on%20a%20mic%20stand%20and%20a%20red%20brick%20wall%20background%2C%20cinematic%20color%20grading%2C%20dramatic%20lighting%2C%20atmospheric%20mood%2C%20dark%20textured%20backdrop%20with%20soft%20glow%2C%20ultra%20detailed%2C%20professional%20film%20still%2C%2016:9%20widescreen%20composition&width=1600&height=900&seq=hero-v5&orientation=landscape",
  a3: "https://readdy.ai/api/search-image?query=Anime%20style%20giant%20robot%20towering%20over%20a%20ruined%20futuristic%20city%20with%20glowing%20blue%20energy%20lines%2C%20cinematic%20color%20grading%2C%20dramatic%20lighting%2C%20atmospheric%20mood%2C%20dark%20textured%20backdrop%20with%20soft%20glow%2C%20ultra%20detailed%2C%20professional%20film%20still%2C%2016:9%20widescreen%20composition&width=1600&height=900&seq=hero-a3&orientation=landscape",
};

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = videos.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (next: number) => setIndex((next + count) % count);
  const current = videos[index];

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[420px] w-full md:h-[560px]">
        {videos.map((video, i) => (
          <div
            key={video.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={heroCovers[video.id] ?? video.cover}
              alt={video.title}
              title={`${video.title} 免费在线观看`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
          </div>
        ))}

        <div className="container-page relative z-10 flex h-full items-center">
          <div key={current.id} className="animate-fade-up max-w-xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/90 px-3 py-1 text-xs font-label font-semibold text-background-50">
              <i className="ri-fire-fill"></i>
              {getCategoryName(current.categoryId)} · 热门推荐
            </span>
            <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
              {current.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400"></i>
                {current.rating} 分
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatPlayCount(current.playCount)} 次播放
              </span>
              <span>{current.year}</span>
              <span>{current.region}</span>
              <span className="rounded-md bg-background-50/15 px-2 py-0.5">{current.type}</span>
            </div>
            <p className="mt-4 line-clamp-2 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
              {current.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${current.categoryId}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                更多{getCategoryName(current.categoryId)}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一个"
          className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:left-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一个"
          className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:right-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {videos.map((video, i) => (
            <button
              key={video.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到第 ${i + 1} 个推荐`}
              className={`h-2 cursor-pointer rounded-full transition-all ${
                i === index ? "w-6 bg-primary-400" : "w-2 bg-background-50/50 hover:bg-background-50/80"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}